import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaChevronLeft,
  FaPencilAlt,
  FaSave,
  FaSpinner,
} from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const EditWritingTaskPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();

  const [level, setLevel] = useState("");
  const [taskNumber, setTaskNumber] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchTask();
  }, [taskId]);

  const fetchTask = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/writing-tasks/${taskId}`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setLevel(data.level);
        setTaskNumber(data.taskNumber.toString());
        setTaskDescription(data.taskDescription);
      } else {
        setError("Failed to load task details");
      }
    } catch (err) {
      console.error("Error fetching task:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!level) {
      setError("Please select a level");
      return;
    }

    if (!taskNumber || parseInt(taskNumber) < 1 || parseInt(taskNumber) > 14) {
      setError("Task number must be between 1 and 14");
      return;
    }

    if (!taskDescription.trim()) {
      setError("Please enter a task description");
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/writing-tasks/${taskId}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            level: level,
            taskNumber: parseInt(taskNumber),
            taskDescription: taskDescription.trim(),
          }),
        }
      );

      if (response.ok) {
        navigate("/admin/writing-tasks");
      } else {
        const data = await response.json();
        setError(
          data.error ||
            "Failed to update writing task. Task number may already exist for this level."
        );
      }
    } catch (err) {
      console.error("Error updating task:", err);
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaPencilAlt />}
        title="Edit Writing Task"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Writing Tasks", to: "/admin/writing-tasks" },
          { label: "Edit" },
        ]}
      />

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <FaPencilAlt className="text-[#009fe3]" />
            Edit Writing Task
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Level Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                German Level *
              </label>
              <select
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                style={{ fontSize: "16px" }}
                required
              >
                <option value="">Select Level</option>
                <option value="A1">A1</option>
                <option value="A2">A2</option>
                <option value="B1">B1</option>
                <option value="B2">B2</option>
                <option value="C1">C1</option>
                <option value="C2">C2</option>
              </select>
            </div>

            {/* Task Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Task Number (1-14) *
              </label>
              <input
                type="number"
                min="1"
                max="14"
                value={taskNumber}
                onChange={(e) => setTaskNumber(e.target.value)}
                placeholder="Enter task number (1-14)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                style={{ fontSize: "16px" }}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Each level has 14 writing tasks. Enter a unique number between 1
                and 14.
              </p>
            </div>

            {/* Task Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Task Description *
              </label>
              <textarea
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                placeholder="Describe the writing task in detail..."
                rows={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all resize-none"
                style={{ fontSize: "16px" }}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Be clear and specific about what students should write about.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave />
                    Save Changes
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate("/admin/writing-tasks")}
                disabled={submitting}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default EditWritingTaskPage;
